import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import type { paths } from "@src/packages/openapi/schema";
import type { ResponseBody } from "@src/packages/openapi/helpers";

type SharingResponse = ResponseBody<
  paths["/patients/{patientId}/sharing"]["get"]
>;

export type SharingRecord = SharingResponse["data"][number];

export type SharingCategory = SharingRecord["category"];

type SharingPatchBody = NonNullable<
  paths["/patients/{patientId}/sharing"]["patch"]["requestBody"]
>["content"]["application/json"];

export type SharingPatchOp = SharingPatchBody[number];

export type DataSharingPermissions = {
  medicalHistory: boolean;
  testResults: boolean;
  medications: boolean;
  cycleData: boolean;
  appointments: boolean;
  documents: boolean;
};

const CATEGORY_TO_PERMISSION: Partial<
  Record<SharingCategory, keyof DataSharingPermissions>
> = {
  history: "medicalHistory",
  test_results: "testResults",
  medications: "medications",
  cycles: "cycleData",
  appointments: "appointments",
  documents: "documents",
};

export const PERMISSION_TO_CATEGORY: Record<
  keyof DataSharingPermissions,
  SharingCategory
> = {
  medicalHistory: "history",
  testResults: "test_results",
  medications: "medications",
  cycleData: "cycles",
  appointments: "appointments",
  documents: "documents",
};

export function usePatientSharing() {
  const { client, patientId } = useApiClient();

  return useQuery({
    queryKey: ["patient-sharing", patientId],
    queryFn: async () => {
      const { data, error, response } = await client.GET(
        "/patients/{patientId}/sharing",
        {
          params: { path: { patientId } },
        },
      );
      if (data) return data.data;
      throwApiError(
        error,
        "Failed to load sharing settings",
        "usePatientSharing",
        response?.status,
      );
    },
    enabled: !!patientId,
  });
}

export function useSharingPermissions(
  targetId: string | null,
): DataSharingPermissions | null {
  const { data: records } = usePatientSharing();
  return useMemo(() => {
    if (!records || !targetId) return null;
    const filtered = records.filter((r) => r.targetId === targetId);
    const perms: DataSharingPermissions = {
      medicalHistory: false,
      testResults: false,
      medications: false,
      cycleData: false,
      appointments: false,
      documents: false,
    };
    for (const record of filtered) {
      const key = CATEGORY_TO_PERMISSION[record.category];
      if (key) {
        perms[key] = record.enabled;
      }
    }
    return perms;
  }, [records, targetId]);
}

export function useUpdatePatientSharing() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ops: SharingPatchBody) => {
      const { data, error, response } = await client.PATCH(
        "/patients/{patientId}/sharing",
        {
          params: { path: { patientId } },
          body: ops,
        },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to update sharing settings",
          "useUpdatePatientSharing",
          response?.status,
        );
      }
      return data;
    },
    onMutate: async (ops) => {
      await queryClient.cancelQueries({
        queryKey: ["patient-sharing", patientId],
      });
      const previous = queryClient.getQueryData<SharingRecord[]>([
        "patient-sharing",
        patientId,
      ]);

      queryClient.setQueryData<SharingRecord[]>(
        ["patient-sharing", patientId],
        (old) => {
          if (!old) return old;
          const result = [...old];
          for (const op of ops) {
            if ("op" in op) continue;
            const idx = result.findIndex(
              (r) => r.targetId === op.targetId && r.category === op.category,
            );
            if (idx >= 0) {
              result[idx] = { ...result[idx]!, enabled: op.enabled };
            } else {
              result.push({
                id: `optimistic-${op.category}`,
                patientId: patientId ?? "",
                targetId: op.targetId,
                targetType: op.targetType,
                category: op.category,
                enabled: op.enabled,
                createdAt: "",
                updatedAt: "",
                updatedBy: null,
              });
            }
          }
          return result;
        },
      );

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          ["patient-sharing", patientId],
          context.previous,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["patient-sharing", patientId],
      });
      queryClient.invalidateQueries({
        predicate: (query) =>
          ["patient", "cycles", "active-cycle", "care-persons"].includes(
            typeof query.queryKey[0] === "string" ? query.queryKey[0] : "",
          ),
      });
    },
  });
}
